import "./UserQuizResult.scss";

import { Alert, Flex, Text } from "@fluentui/react-northstar";
import { formatQuizPoints } from "@teams/client-shared";
import { NominalQuestion, QuestionResponse, TeamsSurveyResponse } from "@teams/types";
import { SurveyDTO } from "@teams/types/api";
import React from "react";

import { PollyIcon } from "../../../../../_shared/components/PollyIcon";

type Props = {
  response: TeamsSurveyResponse;
  survey: SurveyDTO;
};

export const UserQuizResult: React.FC<Props> = ({ response, survey }) => {
  return (
    <Flex column gap="gap.medium">
      {survey.questions.map((question, index) => {
        const userResponse = response.responses.find(r => r.questionId === question.id);

        return (
          <UserQuestionResponse
            key={question.id}
            question={question as NominalQuestion}
            userResponse={userResponse}
            index={index}
          />
        );
      })}
    </Flex>
  );
};

const UserQuestionResponse: React.FC<{
  question: NominalQuestion;
  index: number;
  userResponse: QuestionResponse | undefined;
}> = ({ question, index, userResponse }) => {
  let userResponseValues: string[] = [];
  if (userResponse) {
    userResponseValues = Array.isArray(userResponse.value)
      ? userResponse.value
      : [userResponse.value];
  }
  const pointText = formatQuizPoints(question.pointValue || 1);
  const correctChoiceCount = question.choices.filter(c => c.correct).length;

  return (
    <Flex column gap="gap.small" className="user-question-container">
      <Flex column hAlign="start" gap="gap.small">
        <Flex fill>
          <>
            <Text
              align="start"
              content={`Q${index + 1}.`}
              className="user-question-container__question-no"
            />
            <Text
              align="start"
              content={question.label}
              className="user-question-container__question-label"
            />
          </>
        </Flex>
        {question.allowMultipleChoices && (
          <Text
            content={`Multiple answers may apply (${pointText} total)`}
            className="user-question-container__subtitle"
          />
        )}
      </Flex>

      {question.choices.map(choice => {
        const correctChoiceIndex = question.choices.filter(c => c.correct).indexOf(choice);
        return (
          <Flex column gap="gap.small" key={choice.id}>
            <Alert
              className={`user-quiz-result ${
                userResponseValues.includes(choice.id) && !choice.correct
                  ? "user-quiz-result__wrong"
                  : userResponseValues.includes(choice.id) && choice.correct
                  ? "user-quiz-result__correct"
                  : ""
              }`}
              content={
                <Flex fill vAlign="center">
                  <Flex fill vAlign="start">
                    <>
                      <PollyIcon
                        icon={choice.correct ? "correct-icon" : "incorrect-icon"}
                        style={{ marginRight: "3px", marginTop: "4px" }}
                        className={
                          choice.correct
                            ? "user-quiz-result__choice-correct-icon"
                            : "user-quiz-result__choice-incorrect-icon"
                        }
                        inheritColor
                      />
                      <Text content={choice.value} />
                    </>
                  </Flex>
                  <Flex.Item push>
                    <Flex styles={{ minWidth: "40px;" }}>
                      {question.allowMultipleChoices && choice.correct && (
                        <Text
                          content={<>{`(${correctChoiceIndex + 1}/${correctChoiceCount})`}</>}
                          weight="light"
                        />
                      )}
                      {!question.allowMultipleChoices &&
                        choice.correct &&
                        userResponseValues.includes(choice.id) && (
                          <Text content={<>{pointText}</>} weight="light" />
                        )}
                      {!choice.correct && userResponseValues.includes(choice.id) && (
                        <Text
                          content="0 pt"
                          weight="light"
                          className="user-quiz-result__wrong-text"
                        />
                      )}
                    </Flex>
                  </Flex.Item>
                </Flex>
              }
            />
          </Flex>
        );
      })}

      {question.allowComments && userResponse?.comment && (
        <Alert
          className="user-quiz-result__comment"
          content={
            <Flex>
              <Text content="Comment:&nbsp;" />
              <Text weight="semibold" content={userResponse.comment} />
            </Flex>
          }
        />
      )}
    </Flex>
  );
};
